import { createActorContext } from '@xstate/react';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
    AccessIssueEntryMethodLabel, AccessIssueSolutionLabel, TrackingAction, TrackingActionCategory,
} from '@acp/shared';
import { accessIssuesWizardMachine } from './machine';
import { addTrackingEvent } from '../../../../../hooks/use-tracking-event';

const AccessIssuesWizardMachineContext = createActorContext(accessIssuesWizardMachine);

function AccessIssuesWizardProvider({
    propertyId,
    children,
}: {
    propertyId: number;
    children: React.ReactNode;
}) {
    const queryClient = useQueryClient();

    const handleEntryMethodUnblocked = (
        entryMethod: AccessIssueEntryMethodLabel,
        resolvedEntryMethods: AccessIssueEntryMethodLabel[]
    ) => {
        const isExtraResolution = resolvedEntryMethods
            .filter((method) => method !== entryMethod)
            .length > 0;

        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: isExtraResolution
                ? TrackingAction.AccessIssuesWizardExtraEntryMethodUnblocked
                : TrackingAction.AccessIssuesWizardEntryMethodUnblocked,
            context: {
                propertyId,
                entryMethod,
            },
        });
    };

    const handleModalClosed = () => {
        // Invalidate a whole bunch of queries related to access issues
        void queryClient.invalidateQueries({
            queryKey: ['property', propertyId],
        });

        void queryClient.invalidateQueries({
            queryKey: ['notices'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['access-issues'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['stats'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['dashboard', 'tab', 'urgent-active-notices'],
        });

        void queryClient.invalidateQueries({
            queryKey: ['dashboard', 'tab', 'access-issues'],
        });

        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: TrackingAction.AccessIssuesWizardClosed,
            context: {
                propertyId,
            },
        });
    };

    const handleModalOpened = () => {
        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: TrackingAction.AccessIssuesWizardOpened,
            context: {
                propertyId,
            },
        });
    };

    const handleSolutionPerformed = (
        entryMethod: AccessIssueEntryMethodLabel,
        resolvedEntryMethods: AccessIssueEntryMethodLabel[],
        solutionLabel: AccessIssueSolutionLabel
    ) => {
        const isExtraResolution = resolvedEntryMethods
            .filter((method) => method !== entryMethod)
            .length > 0;

        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: isExtraResolution
                ? TrackingAction.AccessIssuesWizardExtraSolutionPerformed
                : TrackingAction.AccessIssuesWizardSolutionPerformed,
            context: {
                propertyId,
                solutionLabel,
                entryMethod,
            },
        });
    };

    const handleFatalError = (message: string) => {
        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: TrackingAction.AccessIssuesWizardFatalError,
            context: {
                propertyId,
                message,
            },
        });
    };

    const handleResolutionStarted = (
        entryMethod: AccessIssueEntryMethodLabel,
        resolvedEntryMethods: AccessIssueEntryMethodLabel[]
    ) => {
        const isExtraResolution = resolvedEntryMethods
            .filter((method) => method !== entryMethod)
            .length > 0;

        addTrackingEvent({
            category: TrackingActionCategory.AccessIssuesWizard,
            action: isExtraResolution
                ? TrackingAction.AccessIssuesWizardExtraResolutionStarted
                : TrackingAction.AccessIssuesWizardResolutionStarted,
            context: {
                propertyId,
                entryMethod,
            },
        });
    };

    const handleNoAccessIssues = () => addTrackingEvent({
        category: TrackingActionCategory.AccessIssuesWizard,
        action: TrackingAction.AccessIssuesWizardNoAccessIssues,
        context: {
            propertyId,
        },
    });

    const handleNoSolutionsPresented = () => addTrackingEvent({
        category: TrackingActionCategory.AccessIssuesWizard,
        action: TrackingAction.AccessIssuesWizardNoSolutions,
        context: {
            propertyId,
        },
    });

    const handleAccessIssuePresented = () => addTrackingEvent({
        category: TrackingActionCategory.AccessIssuesWizard,
        action: TrackingAction.AccessIssuesWizardAccessIssuePresented,
        context: {
            propertyId,
        },
    });

    const handleAllEntryMethodsDisqualified = () => addTrackingEvent({
        category: TrackingActionCategory.AccessIssuesWizard,
        action: TrackingAction.AccessIssuesWizardAllEntryMethodsDisqualified,
        context: {
            propertyId,
        },
    });

    return (
        <AccessIssuesWizardMachineContext.Provider
            logic={accessIssuesWizardMachine.provide({
                actions: {
                    onEntryMethodUnblocked: (_, params) => handleEntryMethodUnblocked(
                        params.entryMethod,
                        params.resolvedEntryMethods
                    ),
                    onModalClosed: handleModalClosed,
                    onModalOpened: handleModalOpened,
                    onSolutionPerformed: (_, params) => handleSolutionPerformed(
                        params.entryMethod,
                        params.resolvedEntryMethods,
                        params.solutionLabel
                    ),
                    onResolutionStarted: (_, params) => handleResolutionStarted(
                        params.entryMethod,
                        params.resolvedEntryMethods
                    ),
                    onFatalError: (_, params) => handleFatalError(
                        params.message
                    ),
                    onNoAccessIssues: handleNoAccessIssues,
                    onNoSolutionsPresented: handleNoSolutionsPresented,
                    onAccessIssuePresented: handleAccessIssuePresented,
                    onAllEntryMethodsDisqualified: handleAllEntryMethodsDisqualified,
                },
            })}
            options={{
                input: {
                    propertyId,
                },
            }}
        >
            { children}
        </AccessIssuesWizardMachineContext.Provider>
    );
}

export { AccessIssuesWizardProvider, AccessIssuesWizardMachineContext };
